import { useCallback, useLayoutEffect, useState } from 'react';
import { useGuestTimer } from './useGuestTimer';
import { LoginAsFlow } from './LoginAsFlow';
import { DisplayAssetsFormData } from '../../types';

export type LoginAsPersistedState = null | 'active.approved.noAssets' | 'active.approved.viewingAssets';

/**
 * When entering "login as" mode, we need to sync internal flow state to reflect persistent one
 */
const syncInternalState = (loginAsMode: boolean, persistedState: LoginAsPersistedState): LoginAsFlow['state'] => {
  if (!loginAsMode) {
    // still loading or not in LoginAs mode
    return { type: 'inactive' };
  }
  if (persistedState === null) {
    // LoginAs mode, first time in this session
    return { type: 'active.approved.welcome' };
  }
  // user reloaded the page and previously clicked something in modals
  return { type: persistedState };
};

export const useLoginAsFlow = (
  sessionExpirationTime: number | null,
  persistedState: LoginAsPersistedState,
  onTerminate: () => void,
  onRenewal: () => Promise<boolean>,
  onSubmitViewAssetsJustification: (formData: DisplayAssetsFormData) => void
): LoginAsFlow => {
  const loginAsMode = sessionExpirationTime !== null;
  const [state, setState] = useState<LoginAsFlow['state']>(() => syncInternalState(loginAsMode, persistedState));

  useLayoutEffect(() => {
    setState(syncInternalState(loginAsMode, persistedState));
  }, [persistedState, loginAsMode]);

  const onTimerComplete = useCallback(
    () =>
      setState((prevState) => {
        if (prevState.type === 'inactive') {
          return { type: 'inactive' };
        }
        return { type: 'active.askingForRenewal' };
      }),
    []
  );

  const { remainingSeconds } = useGuestTimer(onTimerComplete, sessionExpirationTime);

  if (state.type === 'active.approved.welcome') {
    return {
      state,
      remainingSeconds,
      actions: {
        confirm: () => {
          setState({ type: 'active.approved.noAssets' });
        },
        cancel: onTerminate,
      },
    };
  }
  if (state.type === 'active.askingForRenewal') {
    return {
      state,
      remainingSeconds,
      actions: {
        accept: () => {
          onRenewal() // for reset server timer
            .then(() => setState({ type: 'active.approved.noAssets' }))
            .catch((error: Error) => console.error(`[useLoginAsFlow]: an error occurred when renewing session: ${error.message}`));
        },
        cancel: onTerminate,
      },
    };
  }
  if (state.type === 'active.approved.noAssets') {
    return {
      state,
      remainingSeconds,
      actions: {
        askForDisplayingAssets: () => setState({ type: 'active.approved.askingForViewAssets', formData: { justification: 'other', details: '' } }),
      },
    };
  }
  if (state.type === 'active.approved.askingForViewAssets') {
    return {
      state,
      remainingSeconds,
      actions: {
        updateForm: (data: DisplayAssetsFormData) => setState({ ...state, formData: data }),
        cancel: () => setState({ type: 'active.approved.noAssets' }),
        submit: () => {
          setState({ type: 'active.approved.viewingAssets' });
          onSubmitViewAssetsJustification(state.formData);
        },
      },
    };
  }
  if (state.type === 'active.approved.viewingAssets') {
    return {
      state,
      remainingSeconds,
      actions: { turnDisplayingAssetsOf: () => setState({ type: 'active.approved.noAssets' }) },
    };
  }
  return {
    state,
    remainingSeconds,
    actions: {},
  };
};
